import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import dma from '../data/google-dma-topojson.json';
import {
  generateLegend,
  generateDMABorders,
  generateHoverEffects,
  formatNumber
} from './helpers';
import LineGraph from './LineGraph';
import DownloadTheDataButton from './DownloadTheDataButton';

const defaultDisplayedProperties = (totalReach) => ({
  overallReach: formatNumber(totalReach)
});

const definePowerScale = (max) => {
  return d3.scalePow().exponent(0.5).domain([0, max]).range([0, 1]).clamp(true);
};

const colorInterpolator = d3.interpolate(
  d3.color('#f6e0dc'),
  d3.color('#c44c4a')
);

const colorScale = (value) => {
  if (value === 0) {
    return '#e7e7e7';
  }

  return d3.scaleSequential().domain([0, 1]).interpolator(colorInterpolator)(
    value
  );
};

const Map = ({
  pubsi = [],
  publication = '',
  description = '',
  overallReach = 0,
  timeseries = []
}) => {
  const [selectedPublication, setSelectedPublication] = useState(publication);
  const [displayedProperties, setDisplayedProperties] = useState(
    defaultDisplayedProperties(overallReach)
  );
  const pubSearchInterest = pubsi;
  const timeseriesData = timeseries;

  const mapRef = useRef(null);
  const displayedPropertiesRef = useRef(displayedProperties);

  useEffect(() => {
    displayedPropertiesRef.current = displayedProperties;

    if (displayedProperties.DMA !== undefined) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'mediaScopeDMAMapSelect',
        publication: selectedPublication,
        dma: displayedProperties.DMA,
        eventCategory: 'MediaExplorer',
      });
    }
  }, [displayedProperties]);

  const maxReach = pubSearchInterest.reduce((max, obj) => {
    return obj.Reach > max ? obj.Reach : max;
  }, 0);

  const minReach = pubSearchInterest.reduce((min, obj) => {
    return obj.Reach < min ? obj.Reach : min;
  }, maxReach);

  let powerScale = definePowerScale(maxReach);
  const darkestColor = colorScale(1);

  const svgRef = useRef();
  const zoomRef = useRef();
  const currentTransformRef = useRef(d3.zoomIdentity);

  function zoomIn() {
    const svg = d3.select(svgRef.current);
    const zoom = zoomRef.current;
    const { width, height } = svgRef.current.getBoundingClientRect();
  
    const center = [width / 2, height / 2];
    const newTransform = currentTransformRef.current
      .translate(center[0], center[1])   // move to center
      .scale(1.2)
      .translate(-center[0], -center[1]); // move back
  
    svg.transition().duration(300).call(zoom.transform, newTransform);
  }
  
  function zoomOut() {
    const svg = d3.select(svgRef.current);
    const zoom = zoomRef.current;
    const { width, height } = svgRef.current.getBoundingClientRect();
  
    const center = [width / 2, height / 2];
    const newTransform = currentTransformRef.current
      .translate(center[0], center[1])
      .scale(0.8)
      .translate(-center[0], -center[1]);
  
    svg.transition().duration(300).call(zoom.transform, newTransform);
  }

  const handleDMAClick = (event, d) => {
    const prop = d.properties;
    if (prop.dma_name === 'National') {
      return;
    }

    const currentlySelectedDMA = displayedPropertiesRef.current.DMA;
    const DMA = prop['dma_name'].replace(/,/g, '');

    const currentlySelectedElement = document.querySelector('.selectedDMA');
    if (currentlySelectedElement) {
      currentlySelectedElement.classList.remove('selectedDMA');
    }

    if (currentlySelectedDMA === DMA) {
      setDisplayedProperties(defaultDisplayedProperties(totalReach));
      return;
    } else {
      event.target.classList.add('selectedDMA');
    }

    const reach = prop['reach'] ? formatNumber(prop['reach']) : 0;
    const population = prop['population']
      ? formatNumber(prop['population'])
      : 0;
    const populationOver18 = prop['populationOver18']
      ? formatNumber(prop['populationOver18'])
      : 0;
    const publicInterest = prop['searchInterest']
      ? `${prop['searchInterest']}%`
      : 0;

    setDisplayedProperties({
      DMA,
      reach,
      population,
      populationOver18,
      publicInterest
    });
  };

  useEffect(() => {
    function resize() {
      const width = mapRef.current.clientWidth;
      const height = mapRef.current.clientHeight;

      projection.fitSize(
        [width, height],
        topojson.feature(dma, dma.objects.dma_shp)
      );

      svg.attr('width', width).attr('height', height);

      updateMap();
    }

    const width = mapRef.current.clientWidth;
    const height = mapRef.current.clientHeight;

    const projection = d3
      .geoMercator()
      .fitSize([width, height], topojson.feature(dma, dma.objects.dma_shp));

    const path = d3.geoPath().projection(projection);
    const svg = d3.select(mapRef.current);
    const graticule = d3
      .geoGraticule()
      .extent([
        [-98 - 45, 38 - 45],
        [-98 + 45, 38 + 45]
      ])
      .step([5, 5]);

    svg
      .append('rect')
      .datum(graticule)
      .attr('class', 'background')
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g');
    const nielsen = dma.objects.dma_shp.geometries;

    svgRef.current = mapRef.current;

    const zoom = d3
      .zoom()
      .scaleExtent([0.5, 2])
      .filter(
        (event) => event.type === 'mousedown' || event.type === 'touchstart'
      )
      .on('zoom', (event) => {
        currentTransformRef.current = event.transform;
        g.attr('transform', event.transform);
      });

    zoomRef.current = zoom;
    svg.call(zoom);

    function updateMap() {
      nielsen.forEach((d) => {
        const { dma_name } = d.properties;
        const dma_code = dma_name.replace(/,/g, '');
        const match = pubSearchInterest.find((p) => p.DMA === dma_code);
        let value = match ? match.Reach : undefined;
        if (dma_name === 'Alaska') {
          value = 0;
        }
        d.properties['reach'] = value;
        d.properties['population'] = match ? match.Population : undefined;
        d.properties['populationOver18'] = match
          ? match['Population Over 18']
          : undefined;
        d.properties['searchInterest'] = match
          ? match['Search Interest']
          : undefined;
      });

      const pathId = (dma_name) =>
        selectedPublication ? `dma-${dma_name}` : 'no-pub-selected';

      const paths = g
        .selectAll('path')
        .data(topojson.feature(dma, dma.objects.dma_shp).features)
        .attr('id', (d) =>
          d.properties.dma_name === 'Alaska'
            ? 'national'
            : pathId(d.properties.dma_name)
        );

      if (selectedPublication) {
        generateHoverEffects(
          d3,
          paths,
          path,
          colorScale,
          powerScale,
          handleDMAClick,
          displayedProperties.DMA
        );
      }

      paths.exit().remove();

      // Filter features for zoom in effect
      const filteredFeatures = topojson
        .feature(dma, dma.objects.dma_shp)
        .features.filter((d) => d.properties['Reach'] > 0);

      if (filteredFeatures.length > 0) {
        const bounds = path.bounds({
          type: 'FeatureCollection',
          features: filteredFeatures
        });
        const dx = bounds[1][0] - bounds[0][0];
        const dy = bounds[1][1] - bounds[0][1];
        const x = (bounds[0][0] + bounds[1][0]) / 2;
        const y = (bounds[0][1] + bounds[1][1]) / 2;
        const scale = Math.max(
          1,
          Math.min(8, 0.9 / Math.max(dx / width, dy / height))
        );
        const translate = [width / 2 - scale * x, height / 2 - scale * y];

        svg
          .transition()
          .duration(750)
          .call(
            zoom.transform,
            d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
          );
      }
    }

    updateMap(selectedPublication);
    generateDMABorders(g, dma, topojson, path);
    if (selectedPublication) {
      generateLegend(d3, colorInterpolator, minReach, maxReach);
    }

    resize();
    window.addEventListener('resize', resize);

    // Clean up on component unmount
    return () => window.removeEventListener('resize', resize);
  }, [pubSearchInterest]);

  const handleDownload = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mediaScopeDataDownload',
      eventCategory: 'MediaExplorer',
      publication: selectedPublication,
      dma: displayedProperties.DMA,
    });

    const csv =
      'DMA,Population,Population Over 18,Reach\n' +
      pubSearchInterest
        .map((row) => {
          const values = Object.keys(row)
            .filter(
              (key) =>
                key !== 'publication_description' && key !== 'Search Interest'
            )
            .map((key) => {
              if (key !== 'Search Interest') {
                return row[key];
              }
            });

          return values.join(',');
        })
        .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', selectedPublication + '.csv');
    a.click();
  };

  const handleClearDmaClick = () => {
    setDisplayedProperties(defaultDisplayedProperties(overallReach));

    const currentlySelectedElement = document.querySelector('.selectedDMA');
    if (currentlySelectedElement) {
      currentlySelectedElement.classList.remove('selectedDMA');
    }
  };

  return (
    <div className="grid">
      <div className="grid__col-xs-12 grid__col-lg-6">
        <div className=" publication-information information-card">
          <h3>{publication}</h3>
          <p>{description}</p>
          <div className="publication-stats">
            <strong>Overall Reach</strong>
            <span>{formatNumber(overallReach)} Unique Visitors</span>
          </div>
        </div>
        <div className="ms-map--linegraph information-card u--margin-top-10">
          <h3 className="h6 ms-map--dma-data__header">
            Public Interest Over Time
          </h3>
          <LineGraph data={timeseriesData} lineColor={darkestColor} />
        </div>
      </div>
      <div className="grid__col-xs-12 grid__col-lg-6">
        <div className="publication-map information-card">
          <div className="publication-map--header">
            <h3>Public Interest by DMA</h3>
            {displayedProperties.DMA ? (
              <span onClick={handleClearDmaClick} style={{ cursor: 'pointer' }}>
                Clear Selected DMA
              </span>
            ) : (
              <span>Click on the map to select a DMA</span>
            )}
          </div>
          <div id="map">
            <div className="map__container">
              <div className="map__container--map">
                <div className="map__container--map-wrapper">
                  <svg ref={mapRef} className="ms-map"></svg>
                  <div id="dmatooltip-hover" className="dmatooltip"></div>
                  <div className="ms-map--zoom-controls">
                    <button onClick={zoomIn}>+</button>
                    <button onClick={zoomOut}>−</button>
                  </div>
                </div>
                <div className="map__tooltips u--display-flex u--flex-direction-column-md-max u--justify-content-space-between u--margin-top-30">
                  <div id="dmatooltip" className="dmatooltip information-card">
                    <strong>Hover over a DMA to see it's reach data</strong>
                    <hr></hr>
                  </div>
                  <div className="ms-map--legend information-card">
                    <h4 className="h6">Reach Scale</h4>
                    <svg className="legend"></svg>
                  </div>
                </div>
                {displayedProperties.DMA && (
                  <>
                    <div className="ms-map--dma-data u--margin-top-20">
                      <p className="ms-map--dma-data__header">
                        <span>Designated Market Area (DMA)</span>
                        <span>{displayedProperties.DMA}</span>
                      </p>
                      <p>
                        <span>Reach</span>
                        <span>{displayedProperties.reach}</span>
                      </p>
                      <p>
                        <span>Population</span>
                        <span>{displayedProperties.population}</span>
                      </p>
                      <p>
                        <span>Adult Population</span>
                        <span>{displayedProperties.populationOver18}</span>
                      </p>
                      <p>
                        <span>Public Interest</span>
                        <span>{displayedProperties.publicInterest}</span>
                      </p>
                    </div>
                    <div className="u--margin-top-20">
                      <h3 style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                        Want to see the data?
                      </h3>
                      <p className="u--flex-direction-column">
                        Download the complete data to explore all DMAs in this
                        news source.
                        <DownloadTheDataButton
                          handleDownload={handleDownload}
                        />
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ms-map--dma-data information-card u--padding-0"></div>
    </div>
  );
};

export default Map;
